<template>
  <NuxtLink :to="`/news/${article.slug}`" class="article-card">
    <div class="image-wrapper">
      <img
        v-if="article.preview_image"
        :src="article.preview_image"
        :alt="article.name"
        loading="lazy"
      />
    </div>

    <div class="date">
      <BaseIconSvoi icon="time/calendar" size="s" />
      <span class="date-text">{{ formattedDate }}</span>
    </div>

    <div class="content">
      <h3 class="title">{{ article.name }}</h3>
      <div class="arrow-icon">
        <BaseIconSvoi icon="arrow/right-up" :size="iconSize" />
      </div>
    </div>
  </NuxtLink>
</template>

<script lang="ts" setup>
  import { BaseIconSvoi } from '~/components/base-ui/icon'
  import type { ArticleCard } from '~/models/articles'
  import { toMonthYearDateFormat } from '~/shared/utils/date'

  const props = defineProps<{
    article: ArticleCard
    iconSize?: 's' | 'm' | 'l' | 'xl'
  }>()

  const formattedDate = computed(() => {
    if (!props.article.publicationDate) return ''
    return toMonthYearDateFormat(props.article.publicationDate)
  })
</script>

<style lang="scss" scoped>
  .article-card {
    position: relative;
    display: block;
    text-decoration: none;
    color: inherit;
    border-radius: 16px;
    overflow: hidden;

    &:hover .image-wrapper img {
      transform: scale(1.1);
    }

    > .image-wrapper {
      overflow: hidden;
      border-radius: 16px;

      > img {
        display: block;
        width: 100%;
        height: 204px;
        object-fit: cover;
        transition: all 0.6s ease;
      }
    }

    > .date {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px;

      > .date-text {
        font-size: 14px;
        font-weight: 600;
        line-height: 22px;
        color: $color-potato-60;
      }
    }

    > .content {
      position: relative;
      display: flex;
      gap: 8px;
      padding: 0 8px 8px;

      > .title {
        flex: 1;
        font-size: 18px;
        font-weight: 400;
        line-height: 1.44;
        color: $color-default-black;
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }

      > .arrow-icon {
        display: flex;
        align-items: center;
        flex-shrink: 0;
        width: 24px;
        height: 24px;
        color: $color-kiwi-50;
      }
    }
  }
</style>
